import {Component, EventEmitter, Input, OnDestroy, Output, ChangeDetectorRef} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';

import {CustomInputComponent} from '../../../components/custom-input/custom-input.component';
import {ButtonComponent} from '../../../components/button/button.component';
import {LABEL_CONSTANT, LOGIN_CONSTANT} from '../../../constant/constant';
import {CognitoService} from '../../../core/auth/cognito.service';
import {HttpErrorService} from '../../../core/services/http-error.service';
import {SIGN_IN_STATE} from '../organization-login.component';
import {ControlMessagesComponent} from '../../../components/control-messages/control-messages.component';
import {ValidationService} from '../../../shared/services/validation.service';

export interface IResetPasswordForm {
  code?: FormControl<string | null>;
  password: FormControl<string | null>;
  confirmPassword: FormControl<string | null>;
}
@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [ReactiveFormsModule, CustomInputComponent, ButtonComponent, ControlMessagesComponent],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent implements OnDestroy {
  @Input() public clientId: string = '';
  @Input() public userName: string | undefined;
  @Input() public resetWithCode!: boolean;
  @Output() setSignInState: EventEmitter<string> = new EventEmitter<string>();
  @Output() signInOuput: EventEmitter<any> = new EventEmitter<any>();
  @Output() routeToApplicationGettingDefaultOrgDetails: EventEmitter<void> =
    new EventEmitter<void>();

  private countdownInterval: any;

  LABEL_CONSTANT = LABEL_CONSTANT;
  resetPasswordForm: FormGroup<IResetPasswordForm>;
  resetCodeSent: boolean = false;
  resendDisabled: boolean = false;
  userAllowedTimes = 0;
  maxUserAllowedTimes = 2;
  countdown: number = 0;

  constructor(
    private _cognitoService: CognitoService,
    private _httpErrorService: HttpErrorService,
    private _validationService: ValidationService,
    private _cdr: ChangeDetectorRef
  ) {
    this.resetPasswordForm = new FormGroup<IResetPasswordForm>(
      {
        password: new FormControl(null, Validators.required),
        confirmPassword: new FormControl(null, Validators.required),
      },
      {validators: [ValidationService.passwordMatchValidator]},
    );
  }

  ngOnInit(): void {
    if (this.resetWithCode) {
      this.resetPasswordForm.addControl('code', new FormControl(null, Validators.required));
    }
  }

  public onSubmit(): void {
    if (this.resetPasswordForm.valid) {
      if (this.resetWithCode) {
        this.resetPassword();
      } else {
        this.confirmSignInWithNewPassword();
      }
    } else {
      // shows validation error if the form is not valid
      this._validationService.validateAllFormFields(this.resetPasswordForm);
    }
  }

  public switchToLoginFlow(): void {
    this.setSignInState.emit(SIGN_IN_STATE.SIGN_IN);
  }

  private resetPassword(): void {
    const {code, confirmPassword} = this.resetPasswordForm.getRawValue();
    this._cognitoService
      .confirmResetPassword(this.userName as string, confirmPassword as string, code as string)
      .then(() => {
        this.switchToLoginFlow();
        this._cdr.detectChanges();
      })
      .catch((error: Error) => {
        this._httpErrorService.httpError.set(error.message);
        this._cdr.detectChanges();
      });
  }

  public getCode(): void {
    this._cognitoService.resetPassword(this.userName as string).finally(() => {
      this.resetCodeSent = true;
      this.startCountdown();
      this._cdr.detectChanges();
    });
  }

  private startCountdown(): void {
    this.resetCodeSent = true;
    this.countdown = 30;

    this.countdownInterval = setInterval(() => {
      this.countdown--;

      if (this.countdown <= 0) {
        this.stopCountdown();
      }
    }, 1000);
  }

  private stopCountdown(): void {
    clearInterval(this.countdownInterval);
    this.resetCodeSent = false;
    this.userAllowedTimes++;
  }

  private confirmSignInWithNewPassword(): void {
    const {confirmPassword} = this.resetPasswordForm.value;
    this._cognitoService
      .handleUpdatePassword(confirmPassword as string)
      .then((result) => {
        if (result?.nextStep?.signInStep === LOGIN_CONSTANT.CONTINUE_SIGN_IN_WITH_TOTP_SETUP) {
          this.signInOuput.emit(result.nextStep);
          this.setSignInState.emit(SIGN_IN_STATE.MFA_SETUP);
        } else if (result?.nextStep?.signInStep === LOGIN_CONSTANT.CONFIRM_SIGN_IN_WITH_TOTP_CODE) {
          this.signInOuput.emit(result.nextStep);
          this.setSignInState.emit(SIGN_IN_STATE.MFA_PROMPT);
        } else {
          this.routeToApplicationGettingDefaultOrgDetails.emit();
        }
        this._cdr.detectChanges();
      })
      .catch((error: Error) => {
        this._httpErrorService.httpError.set(error.message);
        this._cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this._httpErrorService.httpError.set('');
    this.stopCountdown();
  }
}
